/** @jsxImportSource @emotion/react */

import React, { useState } from "react";
import {
  EuiPanel,
  EuiText,
  EuiBasicTable,
  EuiBasicTableColumn,
  EuiButtonGroup,
  useEuiTheme,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSpacer,
} from "@inscopix/ideas-eui";
import { WorkflowEditorStoreState } from "components/WorkflowEditor/WorkflowEditorProvider/WorkflowEditor.store";
import { useWorkflowEditorContext } from "components/WorkflowEditor/WorkflowEditorProvider/WorkflowEditorProvider";
import { WorkflowEditorPageGraph } from "../Graph/WorkflowEditorPageGraph";
import { ButtonGroupTableGraph } from "components/WorkflowEditor/components/ButtonGroupTableGraph";

export interface ToolVersion {
  id: number;
  version: string;
}

export interface WorkflowOutputListItem {
  id: string;
  key: string;
  name: string;
  tableName: string;

  outputPolicy: WorkflowEditorStoreState["resultOverrides"][keyof WorkflowEditorStoreState["resultOverrides"]]["outputPolicy"];
}

export interface PanelWorkflowOutputListProps {
  outputs: WorkflowOutputListItem[];
}

export const PanelWorkflowOutputList: React.FC<
  PanelWorkflowOutputListProps
> = ({ outputs }) => {
  const setResultOverride = useWorkflowEditorContext(
    (s) => s.setResultOverride,
  );

  const [view, setView] = useState<"TABLE" | "GRAPH">("TABLE");

  const { euiTheme } = useEuiTheme();

  const columns: Array<EuiBasicTableColumn<WorkflowOutputListItem>> = [
    {
      field: "name",
      name: "Result name",
      render: (_, output) => (
        <EuiText size="s">
          <strong>{output.name}</strong>
        </EuiText>
      ),
    },
    {
      field: "tableName",
      name: "Workflow Step",
      render: (_, output) => (
        <EuiText key="workflow step" size="s">
          {output.tableName}
        </EuiText>
      ),
    },
    {
      field: "outputPolicy",
      name: "Output Policy",
      render: (_, output) => (
        <EuiButtonGroup
          type="single"
          legend="Output policy"
          idSelected={output.outputPolicy}
          options={[
            {
              id: "KEEP" as const,
              label: "Keep",
              "aria-label": "Keep output",
              css: {
                backgroundColor:
                  output.outputPolicy === "KEEP"
                    ? euiTheme.colors.success
                    : euiTheme.colors.lightShade,
              },
            },
            {
              id: "DISCARD" as const,
              label: "Discard",
              "aria-label": "Discard output",
              css: {
                backgroundColor:
                  output.outputPolicy === "DISCARD"
                    ? euiTheme.colors.danger
                    : euiTheme.colors.lightShade,
              },
            },
          ]}
          onChange={(id) => {
            setResultOverride(output.id, (prev) => ({
              ...prev,
              outputPolicy: id === "KEEP" ? "KEEP" : "DISCARD",
            }));
          }}
        />
      ),
    },
  ];

  return (
    <>
      <EuiFlexGroup
        css={{
          width: "100%",
          justifyContent: "flex-end",
        }}
      >
        <EuiFlexItem>
          <ButtonGroupTableGraph selected={view} onChange={setView} />
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer />
      {view === "GRAPH" ? (
        <WorkflowEditorPageGraph withResults />
      ) : (
        <EuiPanel paddingSize="m">
          <EuiBasicTable
            items={outputs}
            columns={columns}
            itemId="rowId"
            noItemsMessage="No tables added yet."
          />
        </EuiPanel>
      )}
    </>
  );
};
